import React, { useEffect, useState, useCallback } from 'react';
import { Table, Tag, Card, Select, Space, message } from 'antd';
import axios from 'axios';
import API_BASE_URL from '../../config/api';
import { usePermission } from '../../context/PermissionContext';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const ACTION_COLORS = {
  LOGIN: 'green',
  LOGIN_FAILED: 'red',
  CREATE: 'blue',
  UPDATE: 'orange',
  DELETE: 'red',
  APPROVE: 'cyan',
  REJECT: 'volcano',
  IMPORT: 'purple',
};

const ACTION_LABELS = {
  LOGIN: 'เข้าสู่ระบบ',
  LOGIN_FAILED: 'เข้าสู่ระบบล้มเหลว',
  CREATE: 'สร้าง',
  UPDATE: 'แก้ไข',
  DELETE: 'ลบ',
  APPROVE: 'อนุมัติ',
  REJECT: 'ปฏิเสธ',
  IMPORT: 'นำเข้า',
};

const formatThaiDateTime = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString('th-TH', {
    year: 'numeric', month: 'short', day: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
};

const AuditLogPage = () => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [filters, setFilters] = useState({ action: undefined, resourceType: undefined, keyword: undefined });
  const { can } = usePermission();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, pageSize });
      if (filters.action) params.set('action', filters.action);
      if (filters.resourceType) params.set('resourceType', filters.resourceType);
      const res = await axios.get(`${API_BASE_URL}/api/audit-logs?${params}`, { headers: authHeaders() });
      setRows(res.data?.rows || []);
      setTotal(res.data?.total || 0);
    } catch {
      message.error('โหลดบันทึกการใช้งานล้มเหลว');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, filters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  if (!can('audit.view')) {
    return <Card style={{ marginTop: 24, textAlign: 'center' }}>คุณไม่มีสิทธิ์เข้าถึงหน้านี้ (ต้องมีสิทธิ์: audit.view)</Card>;
  }

  const columns = [
    { title: 'เวลา', key: 'created_at', width: 160, render: (_, r) => formatThaiDateTime(r.created_at) },
    { title: 'ผู้ใช้', key: 'user', width: 150, render: (_, r) => `${r.username || '-'}${r.user_level ? ` (${r.user_level})` : ''}` },
    {
      title: 'การกระทำ',
      dataIndex: 'action',
      key: 'action',
      width: 140,
      render: (a) => <Tag color={ACTION_COLORS[a] || 'default'}>{ACTION_LABELS[a] || a}</Tag>,
    },
    { title: 'รายการ', dataIndex: 'resource_type', key: 'resource_type', width: 150 },
    { title: 'ชื่อ/รายละเอียด', key: 'name', render: (_, r) => `${r.resource_name || ''}${r.resource_id ? ` (#${r.resource_id})` : ''}` },
    {
      title: 'รายละเอียด',
      key: 'details',
      width: 220,
      render: (_, r) => {
        if (!r.details) return '';
        try {
          const obj = JSON.parse(r.details);
          return <span style={{ fontSize: 12, color: '#888' }}>{JSON.stringify(obj).slice(0, 120)}</span>;
        } catch {
          return <span style={{ fontSize: 12, color: '#888' }}>{String(r.details).slice(0, 120)}</span>;
        }
      },
    },
    { title: 'IP', dataIndex: 'ip_address', key: 'ip', width: 130 },
  ];

  return (
    <div>
      <h2>บันทึกการใช้งาน (Audit Log)</h2>

      <Space wrap style={{ marginBottom: 16 }}>
        <Select
          placeholder="การกระทำ"
          allowClear
          style={{ width: 150 }}
          value={filters.action}
          onChange={(v) => { setFilters((f) => ({ ...f, action: v })); setPage(1); }}
          options={Object.entries(ACTION_LABELS).map(([v, l]) => ({ value: v, label: l }))}
        />
        <Select
          placeholder="รายการ"
          allowClear
          style={{ width: 150 }}
          value={filters.resourceType}
          onChange={(v) => { setFilters((f) => ({ ...f, resourceType: v })); setPage(1); }}
          options={['temple', 'monk', 'news', 'category', 'user', 'user_permissions', 'user_access', 'temple_statistics', 'role', 'site_settings', 'auth'].map((v) => ({ value: v, label: v }))}
        />
      </Space>

      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        loading={loading}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          onChange: (p, ps) => { setPage(p); setPageSize(ps); },
        }}
        scroll={{ x: 1100 }}
      />
    </div>
  );
};

export default AuditLogPage;
